import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useAuth } from '../../../auth/AuthProvider';
import { Money } from '../../../components/Money';
import { useVariantUnits, type TaxRate } from '../lib/api';
import type { CartAction, CartLine } from '../lib/cart';
import { dec, isDecimal, mul, toPlain } from '../lib/decimal';
import type { LineTotals } from '../lib/totals';
import { ItemTile } from '../counter/bits';
import { DiscountInput } from './DiscountInput';

const COLUMNS = {
  md: '110px 120px 150px minmax(170px,1fr) 36px',
  tax: '110px 120px 150px minmax(170px,1fr) 150px 36px',
};

function Row({
  line,
  total,
  taxRates,
  taxEnabled,
  dispatch,
}: {
  line: CartLine;
  total: LineTotals | undefined;
  taxRates: TaxRate[];
  taxEnabled: boolean;
  dispatch: (a: CartAction) => void;
}) {
  const { can } = useAuth();
  const units = useVariantUnits(line);
  const update = (patch: Partial<CartLine>) => dispatch({ type: 'update', key: line.key, patch });
  const qtyError = !isDecimal(line.qty) || dec(line.qty) <= 0n;
  const priceError = !isDecimal(line.unitPrice);
  const below = !priceError && dec(line.unitPrice) < dec(line.listPrice);
  const showTax = taxEnabled && taxRates.length > 0;
  const unitList = units.length
    ? units
    : [{ unitId: line.unitId, symbol: line.unitSymbol, factor: line.factor, isDefaultSale: true }];

  return (
    <Box
      role="row"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr 1fr',
          md: showTax ? COLUMNS.tax : COLUMNS.md,
        },
        gridTemplateAreas: {
          xs: showTax
            ? '"item item" "qty unit" "price disc" "tax tax"'
            : '"item item" "qty unit" "price disc"',
          md: showTax
            ? '"item item item item item del" "qty unit price disc tax ."'
            : '"item item item item del" "qty unit price disc ."',
        },
        columnGap: 1.5,
        rowGap: 1.5,
        alignItems: 'start',
        px: 2.5,
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          gridArea: 'item',
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          minWidth: 0,
          pr: { xs: 5, md: 0 },
        }}
      >
        <ItemTile name={line.name} size={40} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {line.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {line.sku}
            {line.factor !== '1' ? ` · ${toPlain(dec(line.factor))} per ${line.unitSymbol}` : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography className="tabular" sx={{ fontWeight: 650 }}>
            <Money value={total?.total ?? '0'} />
          </Typography>
          {total && dec(total.tax) > 0n && (
            <Typography variant="caption" color="text.secondary">
              incl. <Money value={total.tax} /> tax
            </Typography>
          )}
        </Box>
      </Box>
      <TextField
        size="small"
        label="Qty"
        value={line.qty}
        onChange={(e) =>
          dispatch({ type: 'setQty', key: line.key, qty: e.target.value.replace(/[^\d.]/g, '') })
        }
        onFocus={(e) => e.target.select()}
        error={qtyError}
        sx={{ gridArea: 'qty' }}
        slotProps={{
          htmlInput: {
            inputMode: 'decimal',
            className: 'tabular',
            'aria-label': `Quantity of ${line.name}`,
          },
        }}
      />
      <TextField
        select
        size="small"
        label="Unit"
        value={line.unitId}
        disabled={unitList.length <= 1}
        onChange={(e) => {
          const u = unitList.find((x) => x.unitId === e.target.value);
          if (!u) return;
          const list = toPlain(mul(dec(line.basePrice), dec(u.factor)));
          update({
            unitId: u.unitId,
            unitSymbol: u.symbol,
            factor: u.factor,
            listPrice: list,
            unitPrice: list,
          });
        }}
        sx={{ gridArea: 'unit' }}
      >
        {unitList.map((u) => (
          <MenuItem key={u.unitId} value={u.unitId}>
            {u.symbol}
            {u.factor !== '1' ? ` ×${toPlain(dec(u.factor))}` : ''}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Price"
        value={line.unitPrice}
        onChange={(e) => update({ unitPrice: e.target.value.replace(/[^\d.]/g, '') })}
        onFocus={(e) => e.target.select()}
        error={priceError || (below && !can('sales.discount'))}
        helperText={below ? 'Below list' : undefined}
        sx={{ gridArea: 'price' }}
        slotProps={{
          htmlInput: {
            inputMode: 'decimal',
            className: 'tabular',
            'aria-label': `Price of ${line.name}`,
          },
        }}
      />
      <Box sx={{ gridArea: 'disc' }}>
        <DiscountInput
          size="small"
          label="Discount"
          value={line.discount}
          disabled={!can('sales.discount')}
          error={!!total?.error}
          onChange={(discount) => update({ discount })}
        />
      </Box>
      {showTax && (
        <TextField
          select
          size="small"
          label="Tax"
          value={line.taxRateId ?? ''}
          onChange={(e) => {
            const t = taxRates.find((r) => r.id === e.target.value);
            update({ taxRateId: t?.id ?? null, taxRate: t?.rate ?? '0' });
          }}
          sx={{ gridArea: 'tax' }}
        >
          <MenuItem value="">No tax</MenuItem>
          {taxRates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Tooltip title="Remove line">
        <IconButton
          size="small"
          aria-label={`Remove ${line.name}`}
          onClick={() => dispatch({ type: 'remove', key: line.key })}
          sx={{
            gridArea: { md: 'del' },
            position: { xs: 'absolute', md: 'static' },
            top: 14,
            right: 12,
            alignSelf: 'center',
            '&:hover': { color: 'error.main' },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** Editable item lines for invoices and orders: qty, unit, price, discount and tax inline. */
export function LineGrid({
  lines,
  totals,
  taxRates,
  taxEnabled,
  dispatch,
}: {
  lines: CartLine[];
  totals: LineTotals[];
  taxRates: TaxRate[];
  taxEnabled: boolean;
  dispatch: (a: CartAction) => void;
}) {
  return (
    <Box role="table" aria-label="Lines">
      {lines.map((l, i) => (
        <Row
          key={l.key}
          line={l}
          total={totals[i]}
          taxRates={taxRates}
          taxEnabled={taxEnabled}
          dispatch={dispatch}
        />
      ))}
    </Box>
  );
}
